import { LeftOutlined, WarningOutlined } from '@ant-design/icons'
import { Link, useLocation } from '@remix-run/react'
import { Button, Flex } from 'antd'
import React from 'react'
import { AgentKey } from '~/agents/common/common/types'
import { AgentFacade } from '~/agents/facade/client'
import { OrganizationMemberFromContext } from '~/core/context/organization/hooks/useOrganizationMembers'
import { DesignSystem } from '~/core/designSystem'
import { AppSessionType } from '~/features/appSession/common'
import { OrganizationType } from '~/features/organization/common'
import { Icon } from '~/features/shared/icons'
import { DataApp } from '../../hooks/useData'
import { TabItem } from '../TabItem'
import { useComputed } from './useComputed'

type Props = {
  conversationId: string
  app: DataApp
  appSession: AppSessionType
  organization: OrganizationType
  organizationMemberAgent: OrganizationMemberFromContext
  notificationCounters: Record<string, number>
  onEdit: () => void
}

export const Tabs: React.FC<Props> = ({
  conversationId,
  app,
  appSession,
  organization,
  organizationMemberAgent,
  notificationCounters,
  onEdit,
}) => {
  const location = useLocation()

  const designSystem = DesignSystem.use()

  const computed = useComputed({ app, appSession })

  const pathBase = `/organizations/${organization.id}/chat/${conversationId}`

  const constants = AgentFacade.getByKey(app.key as AgentKey)

  const hasSettingsTab = constants?.agentCommon?.Agent?.hasSettings ?? false

  const tabs =
    constants?.agentClient?.Agent?.getTabs({ appSession, pathBase }) ?? []

  const isPathMatching = (path: string) => location.pathname.startsWith(path)

  const isPathExact = (path: string) => location.pathname === path

  return (
    <>
      <Flex
        gap={8}
        align="center"
        style={{ whiteSpace: 'nowrap', overflowX: 'auto' }}
      >
        {designSystem.isMobile && (
          <Link to={`/organizations/${organization.id}/chat`}>
            <Button icon={<LeftOutlined />} type="text" size="small" />
          </Link>
        )}

        {hasSettingsTab && !designSystem.isMobile && (
          <Button
            icon={
              computed.isOnboardingCorrupted ? (
                <WarningOutlined />
              ) : (
                <Icon name="adjustments-horizontal" />
              )
            }
            variant="filled"
            color={computed.isOnboardingCorrupted ? 'orange' : 'default'}
            onClick={onEdit}
          >
            Edit
          </Button>
        )}

        <TabItem
          icon={<Icon name="chat" />}
          to={`${pathBase}`}
          isActive={isPathExact(pathBase)}
        >
          Messages
        </TabItem>

        {tabs.map((tab, index) => (
          <TabItem
            key={`${app.key}-${index}`}
            icon={tab.icon}
            to={tab.to}
            isActive={isPathMatching(tab.to)}
          >
            {designSystem.isMobile ? '' : tab.label}
          </TabItem>
        ))}
      </Flex>
    </>
  )
}
